import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import {
  Plus, IndianRupee, Package, Download, Filter,
  Eye, X, Trash2, Edit2, FileSpreadsheet, FileText,
  ChevronDown, AlertCircle, CheckCircle2, Clock,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./material.css";

/* ─── helpers ─────────────────────────────────────── */
const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}`;

/* ─── tiny sub-components ─────────────────────────── */
const Badge = ({ children, variant = "neutral" }) => {
  const variants = {
    neutral: "mat-badge--neutral",
    teal:    "mat-badge--teal",
    amber:   "mat-badge--amber",
    red:     "mat-badge--red",
  };
  return (
    <span className={`mat-badge ${variants[variant]}`}>
      {children}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, variant }) => (
  <div className={`stat-card stat-card--${variant}`}>
    <div className="stat-card__icon">
      <Icon size={18} />
    </div>
    <div>
      <p className="stat-card__label">{label}</p>
      <p className="stat-card__value">{value}</p>
    </div>
  </div>
);

/* ─── main component ──────────────────────────────── */
export default function Material() {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const userData = JSON.parse(localStorage.getItem("NagpurProperties")) || {};
  const token = userData?.token;
  const role = userData?.role?.[0]?.roleName || "SuperEngineer";

  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [fullImageUrl, setFullImageUrl] = useState(null);
  const [selectedSite, setSelectedSite] = useState("");
  const [priceModalData, setPriceModalData] = useState(null);

  // Keep a static list of all sites (fetched once without filter)
  const [allSites, setAllSites] = useState([]);

  // Payment related states
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [selectedPaymentSite, setSelectedPaymentSite] = useState("");

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);

  const [paymentFormData, setPaymentFormData] = useState({
    amount: "",
    paymentMode: "",
    remark: "",
    siteName: "",
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const numericVendorId = vendorId ? Number(vendorId) : NaN;
  const isInvalidVendorId = !vendorId || isNaN(numericVendorId) || numericVendorId <= 0;

  const isEngineer = role === "Engineer";
  const isSuperEngineer = role === "SuperEngineer";
  const isAdmin = role === "Admin";
  const isSuperAdmin = role === "SuperAdmin";
  const canPay = isSuperEngineer || isAdmin || isSuperAdmin;

  /* ─── Fetch Site-wise Summary ───────────────────────── */
  const fetchMaterials = useCallback(
    async (site = selectedSite, page = currentPage, size = pageSize) => {
      try {
        setLoading(true);
        setError(null);
        if (!token) {
          setError("Please log in again");
          return;
        }

        const params = { page, size };
        if (site) params.siteName = site;

        const res = await axiosInstance.get(
          `/DailyMaterial/${numericVendorId}/site-wise-summary`,
          { headers: { Authorization: `Bearer ${token}` }, params }
        );

        setAccountData(res.data);

        if (res.data?.totalPages !== undefined) {
          setTotalPages(res.data.totalPages);
        } else if (res.data?.dailyMaterials?.length !== undefined) {
          const items = res.data.dailyMaterials || [];
          if (items.length < size && page === 0) {
            setTotalPages(1);
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load materials");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, numericVendorId]
  );

  /* ─── Fetch all sites once ─── */
  const fetchAllSites = useCallback(async () => {
    if (!token || isInvalidVendorId) return;
    try {
      const res = await axiosInstance.get(
        `/DailyMaterial/${numericVendorId}/site-wise-summary`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 0, size: 200 },
        }
      );
      const items = res.data?.dailyMaterials || [];
      const sites = [...new Set(items.map((d) => d.siteName).filter(Boolean))].sort();
      setAllSites(sites);
    } catch {
      // silently fail
    }
  }, [token, numericVendorId, isInvalidVendorId]);

  useEffect(() => {
    if (!isInvalidVendorId) {
      fetchAllSites();
    }
  }, [fetchAllSites, isInvalidVendorId]);

  useEffect(() => {
    if (!isInvalidVendorId) {
      fetchMaterials(selectedSite, currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, selectedSite, currentPage, pageSize, isInvalidVendorId]);

  /* ─── Site filter change handler ─── */
  const handleSiteChange = (site) => {
    setSelectedSite(site);
    setCurrentPage(0);
  };

  /* ─── Fetch Payments ───────────────────────────────── */
  const fetchVendorPayments = async (siteName = "") => {
    if (!vendorId) return;

    try {
      setLoadingPayments(true);

      const res = await axiosInstance.get(`/DailyMaterial/vendor/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let payments = res.data || [];

      payments = payments.map((p) => ({
        paymentId: p.paymentId || p.id || "—",
        vendorName: p.vendorName || accountData?.vendorName || "Unknown Vendor",
        siteName: p.siteName || "—",
        amount: Number(p.amount) || 0,
        paymentMode: p.paymentMode || "—",
        remark: p.remark || "—",
        paymentDate:
          p.paymentDate || p.date || new Date().toISOString().split("T")[0],
      }));

      setVendorPayments(payments);

      if (siteName) {
        setFilteredPayments(payments.filter((p) => p.siteName === siteName));
      } else {
        setFilteredPayments(payments);
      }
    } catch (err) {
      console.error(err);
      setVendorPayments([]);
      setFilteredPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  /* ─── Payment CRUD ───────────────────────────────── */
  const handleSavePayment = async () => {
    if (
      !paymentFormData.amount ||
      isNaN(paymentFormData.amount) ||
      Number(paymentFormData.amount) <= 0
    ) {
      alert("Please enter a valid amount greater than 0");
      return;
    }

    try {
      setSubmittingPayment(true);

      const payload = {
        vendorId: Number(vendorId),
        amount: Number(paymentFormData.amount),
        paymentMode: paymentFormData.paymentMode.trim(),
        remark: paymentFormData.remark.trim(),
        siteName: paymentFormData.siteName.trim(),
      };

      if (isEditingPayment) {
        await axiosInstance.put(
          `/DailyMaterial/edit/${currentPaymentId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert("Payment updated successfully!");
      } else {
        await axiosInstance.post(`/DailyMaterial/vendor/payment`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Payment recorded successfully!");
      }

      setPaymentFormData({ amount: "", paymentMode: "", remark: "", siteName: "" });
      setShowPaymentForm(false);
      setIsEditingPayment(false);
      setCurrentPaymentId(null);

      fetchVendorPayments(selectedPaymentSite);
      fetchMaterials(selectedSite, currentPage, pageSize);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save payment");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleEditPayment = (payment) => {
    setPaymentFormData({
      amount: payment.amount.toString(),
      paymentMode: payment.paymentMode || "",
      remark: payment.remark || "",
      siteName: payment.siteName || "",
    });
    setCurrentPaymentId(payment.paymentId);
    setIsEditingPayment(true);
    setShowPaymentForm(true);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment? This cannot be undone.")) return;
    try {
      await axiosInstance.delete(
        `/DailyMaterial/delete-payment/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Payment deleted!");
      fetchVendorPayments(selectedPaymentSite);
      fetchMaterials(selectedSite, currentPage, pageSize);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete payment");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.dailyMaterialId) return;
    try {
      await axiosInstance.delete(
        `/DailyMaterial/delete/${deleteTarget.dailyMaterialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  /* ─── EXPORT PAYMENTS TO EXCEL ─── */
  const exportPaymentsToExcel = () => {
    const payments = filteredPayments;
    if (!payments.length) {
      alert("No payment data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const vendorName =
      accountData?.vendorName || payments[0]?.vendorName || "Vendor";

    const totalPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const totalMaterial = accountData?.totalAmount || 0;
    const balance = totalMaterial - totalPaid;

    const summaryRows = [
      ["Vendor Payment Report"],
      [],
      ["Generated on", new Date().toLocaleString("en-IN")],
      ["Vendor Name", vendorName],
      ["Site Filter Applied", selectedPaymentSite || "All Sites"],
      [],
      ["SUMMARY"],
      ["Total Material Amount (₹)", totalMaterial.toLocaleString("en-IN")],
      ["Total Paid Amount (₹)", totalPaid.toLocaleString("en-IN")],
      ["Balance Amount (₹)", balance.toLocaleString("en-IN")],
      [],
      ["Payment Count", payments.length],
    ];

    const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
    summaryWs["!cols"] = [{ wch: 30 }, { wch: 25 }];
    XLSX.utils.book_append_sheet(workbook, summaryWs, "Summary");

    const detailRows = [
      ["ID", "Vendor", "Site", "Amount (₹)", "Mode", "Remark", "Date"],
      ...payments.map((p) => [
        p.paymentId,
        p.vendorName || vendorName,
        p.siteName || "—",
        Number(p.amount).toLocaleString("en-IN"),
        p.paymentMode || "—",
        p.remark || "—",
        p.paymentDate || "—",
      ]),
      [],
      [],
      ["TOTALS AND SUMMARY", "", "", "", "", "", ""],
      ["", "", "TOTAL PAID:", `₹${totalPaid.toLocaleString("en-IN")}`, "", "", ""],
      ["", "", "TOTAL MATERIAL:", `₹${totalMaterial.toLocaleString("en-IN")}`, "", "", ""],
      ["", "", "BALANCE DUE:", `₹${balance.toLocaleString("en-IN")}`, "", "", ""],
    ];

    const detailWs = XLSX.utils.aoa_to_sheet(detailRows);
    detailWs["!cols"] = [
      { wch: 12 }, { wch: 25 }, { wch: 30 },
      { wch: 18 }, { wch: 15 }, { wch: 35 }, { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(workbook, detailWs, "Payment Details");

    const siteWiseData = new Map();
    payments.forEach((p) => {
      const site = p.siteName || "No Site";
      if (!siteWiseData.has(site)) siteWiseData.set(site, { total: 0, count: 0 });
      const cur = siteWiseData.get(site);
      cur.total += Number(p.amount);
      cur.count += 1;
    });

    const siteWiseRows = [
      ["SITE-WISE PAYMENT SUMMARY"],
      [],
      ["Site Name", "Number of Payments", "Total Amount (₹)"],
      ...Array.from(siteWiseData.entries()).map(([site, data]) => [
        site,
        data.count,
        data.total.toLocaleString("en-IN"),
      ]),
      [],
      ["GRAND TOTAL", payments.length, totalPaid.toLocaleString("en-IN")],
    ];

    const siteWiseWs = XLSX.utils.aoa_to_sheet(siteWiseRows);
    siteWiseWs["!cols"] = [{ wch: 35 }, { wch: 20 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, siteWiseWs, "Site-wise Summary");

    XLSX.writeFile(
      workbook,
      `Vendor_Payment_Report_${vendorName.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  };

  /* ─── Other Exports ───────────────────────────────────── */
  const exportToExcel = () => {
    if (!accountData) { alert("No data loaded yet"); return; }
    const workbook = XLSX.utils.book_new();
    const rows = [];
    rows.push(["Material Account Summary"]);
    rows.push(["Total Amount", fmtINR(accountData.totalAmount)]);
    rows.push(["Paid Amount", fmtINR(accountData.paidAmount)]);
    rows.push(["Balance Amount", fmtINR(accountData.balanceAmount)]);
    if (accountData.siteName) rows.push(["Site", accountData.siteName]);
    rows.push([]);
    rows.push(["Exported on", new Date().toLocaleString("en-IN")]);
    rows.push([]);
    rows.push([
      "DM No", "Date", "Time", "Vehicle No", "Site", "Supplier", "Engineer", "Remark",
      "Material Name", "Quantity", "Unit", "Size/Type", "Price/Unit (₹)", "Total (₹)",
    ]);

    (accountData.dailyMaterials || []).forEach((dm) => {
      const base = [
        dm.dmNo || `DM-${dm.dailyMaterialId}`,
        dm.date || "-",
        dm.time
          ? `${String(dm.time.hour).padStart(2, "0")}:${String(dm.time.minute).padStart(2, "0")}`
          : "-",
        dm.vehicleNo || "-",
        dm.siteName || "-",
        dm.supplierName || "-",
        dm.engineerName || "-",
        dm.remark || "-",
      ];

      if (!dm.materialItems?.length) {
        rows.push([...base, "", "", "", "", "", ""]);
        return;
      }

      dm.materialItems.forEach((it, idx) => {
        rows.push(
          idx === 0
            ? [
                ...base,
                it.dailyMaterialName || "",
                it.materialQty || "",
                it.unit || "",
                it.size || "",
                it.price ? it.price.toLocaleString("en-IN") : "Not set",
                it.total ? it.total.toLocaleString("en-IN") : "",
              ]
            : [
                "", "", "", "", "", "", "", "",
                it.dailyMaterialName || "",
                it.materialQty || "",
                it.unit || "",
                it.size || "",
                it.price ? it.price.toLocaleString("en-IN") : "Not set",
                it.total ? it.total.toLocaleString("en-IN") : "",
              ]
        );
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [12, 12, 10, 14, 20, 18, 22, 30, 20, 10, 10, 12, 14, 14].map((w) => ({
      wch: w,
    }));
    XLSX.utils.book_append_sheet(workbook, ws, "Material Deliveries");
    XLSX.writeFile(
      workbook,
      `Material_Deliveries_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const exportToPDF = () => {
    const materials = accountData?.dailyMaterials || [];
    if (!materials.length) { alert("No data to export"); return; }
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(18);
    doc.text("Material Deliveries Report", 14, 15);
    doc.setFontSize(11);
    doc.text(`Exported on: ${new Date().toLocaleString("en-IN")}`, 14, 22);
    if (accountData.siteName) doc.text(`Site: ${accountData.siteName}`, 14, 29);

    const body = [];
    materials.forEach((dm) =>
      dm.materialItems?.forEach((it) =>
        body.push([
          dm.dmNo || `DM-${dm.dailyMaterialId}`,
          dm.date || "-",
          dm.siteName || "-",
          dm.vehicleNo || "-",
          dm.supplierName || "-",
          it.dailyMaterialName || "-",
          it.materialQty || 0,
          it.unit || "-",
          it.price || 0,
          it.total || 0,
        ])
      )
    );

    autoTable(doc, {
      startY: accountData.siteName ? 36 : 30,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      head: [
        ["DM No", "Date", "Site", "Vehicle", "Supplier", "Material", "Qty", "Unit", "Rate", "Item Total"],
      ],
      body,
      headStyles: { fillColor: [15, 110, 86], textColor: 255 },
    });
    doc.save(`Material_Deliveries_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  /* ─── Helper: format time object from API ─── */
  const formatTime = (time) => {
    if (!time) return null;
    if (typeof time === "string") return time.substring(0, 5);
    if (typeof time === "object" && time.hour !== undefined) {
      return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
    }
    return null;
  };

  /* ─── Guards ─────────────────────────────────────── */
  if (isInvalidVendorId) {
    return (
      <div className="mat-empty-page">
        <Package size={64} className="mat-empty-page__icon" />
        <h2>No vendor selected</h2>
        <p>Choose a vendor from the list to view their material deliveries.</p>
        <Link to="/vendors" className="mat-btn mat-btn--primary">
          ← Back to vendors
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mat-loading">
        <div className="mat-spinner" />
        <span>Loading deliveries…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mat-error-page">
        <AlertCircle size={40} />
        <p>{error}</p>
      </div>
    );
  }

  const materialAccount = accountData || null;
  const dailyMaterials = accountData?.dailyMaterials || [];
  const sites = allSites;

  /* ─── Render ─────────────────────────────────────── */
  return (
    <div className="mat-page">
      {/* Header */}
      <div className="mat-header">
        <div className="mat-header__left">
          <h1 className="mat-header__title">Material deliveries</h1>
          <p className="mat-header__sub">
            {selectedSite
              ? `Showing site: ${selectedSite}`
              : "Track all entries and vendor payments"}
          </p>
        </div>
        <div className="mat-header__actions">
          <div className="mat-filter">
            <Filter size={14} />
            <select
              value={selectedSite}
              onChange={(e) => handleSiteChange(e.target.value)}
            >
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <ChevronDown size={13} className="mat-filter__chevron" />
          </div>

          {canPay && (
            <button
              className="mat-btn mat-btn--primary"
              onClick={() => {
                setShowPaymentHistory(true);
                setSelectedPaymentSite("");
                fetchVendorPayments("");
              }}
              disabled={loading}
            >
              <IndianRupee size={15} /> Record payment
            </button>
          )}

          <button
            className="mat-btn mat-btn--ghost"
            onClick={exportToExcel}
            disabled={!dailyMaterials.length}
          >
            <FileSpreadsheet size={15} /> Excel
          </button>

          <button
            className="mat-btn mat-btn--ghost"
            onClick={exportToPDF}
            disabled={!dailyMaterials.length}
          >
            <FileText size={15} /> PDF
          </button>

          <Link
            to={`/add-material/${vendorId}`}
            className="mat-btn mat-btn--fab"
            title="Add delivery"
          >
            <Plus size={20} />
          </Link>
        </div>
      </div>

      {/* Stat Cards */}
      {!isEngineer && materialAccount && (
        <div className="mat-stats">
          <StatCard
            label={selectedSite ? `Total (${selectedSite})` : "Total amount"}
            variant="neutral"
            icon={Package}
            value={fmtINR(materialAccount.totalAmount)}
          />
          <StatCard
            label="Paid amount"
            variant="success"
            icon={CheckCircle2}
            value={fmtINR(materialAccount.paidAmount)}
          />
          <StatCard
            label="Balance due"
            variant="danger"
            icon={Clock}
            value={fmtINR(materialAccount.balanceAmount)}
          />
        </div>
      )}

      {/* Main Table */}
      {dailyMaterials.length === 0 ? (
        <div className="mat-empty">
          <Package size={56} className="mat-empty__icon" />
          <h3>No deliveries found</h3>
          <p>
            {selectedSite
              ? `No entries for site "${selectedSite}"`
              : "No material entries for this vendor yet"}
          </p>
        </div>
      ) : (
        <>
          <div className="mat-table-wrap">
            <table className="mat-table">
              <thead>
                <tr>
                  <th>DM No.</th>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th>Site</th>
                  <th>Supplier</th>
                  {/* ── NEW: expanded materials column ── */}
                  <th>Materials</th>
                  {!isEngineer && <th style={{ textAlign: "right" }}>Total</th>}
                  <th style={{ textAlign: "center" }}>Image</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dailyMaterials.map((item) => {
                  const total =
                    item.materialItems?.reduce(
                      (s, i) => s + (Number(i.total) || 0),
                      0
                    ) || 0;
                  const imageUrl = item.vehicalImg?.startsWith("http")
                    ? item.vehicalImg
                    : `${process.env.REACT_APP_BASE_URL || ""}/images/${item.vehicalImg}`;
                  const timeStr = formatTime(item.time);

                  return (
                    <tr key={item.dailyMaterialId || item.id}>
                      {/* DM No */}
                      <td>
                        <span className="mat-dm-no">
                          {item.dmNo || `DM-${item.dailyMaterialId || item.id}`}
                        </span>
                      </td>

                      {/* Date */}
                      <td>
                        <span className="mat-date">{item.date || "—"}</span>
                        {timeStr && (
                          <span className="mat-time">{timeStr}</span>
                        )}
                      </td>

                      {/* Vehicle */}
                      <td>
                        <Badge variant="neutral">{item.vehicleNo || "—"}</Badge>
                      </td>

                      {/* Site */}
                      <td className="mat-td-site">{item.siteName || "—"}</td>

                      {/* Supplier */}
                      <td className="mat-td-supplier">{item.supplierName || "—"}</td>

                      {/* ── Materials list (NEW) ── */}
                      <td>
                        {item.materialItems?.length ? (
                          <div className="mat-items-list">
                            {item.materialItems.map((mat, idx) => (
                              <div key={idx} className="mat-item-chip">
                                <span className="mat-item-name">
                                  {mat.dailyMaterialName || "—"}
                                </span>
                                <span className="mat-item-meta">
                                  qty: {mat.materialQty} |
                                  unit: {mat.unit ? ` ${mat.unit}` : ""}|
                                  size: {mat.size ? ` · ${mat.size}` : ""}|
                                  {!isEngineer && mat.price
                                    ? ` · ₹${Number(mat.price).toLocaleString("en-IN")}/unit`
                                    : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="mat-no-img">—</span>
                        )}
                      </td>

                      {/* Total */}
                      {!isEngineer && (
                        <td className="mat-amount">{fmtINR(total)}</td>
                      )}

                      {/* Image */}
                      <td style={{ textAlign: "center" }}>
                        {item.vehicalImg ? (
                          <img
                            src={imageUrl}
                            alt="Vehicle"
                            className="mat-thumb"
                            onClick={() => setFullImageUrl(imageUrl)}
                          />
                        ) : (
                          <span className="mat-no-img">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="mat-icon-btn"
                          onClick={() => setSelectedDelivery(item)}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mat-pagination">
              <button
                className="mat-btn mat-btn--ghost mat-btn--icon"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="mat-pagination__label">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                className="mat-btn mat-btn--ghost mat-btn--icon"
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Modals ── */}
      {selectedDelivery && (
        <DeliveryDetailModal
          delivery={selectedDelivery}
          role={role}
          isEngineer={isEngineer}
          onClose={() => setSelectedDelivery(null)}
          onEdit={() => {
            navigate(
              `/edit-material/${selectedDelivery.dailyMaterialId || selectedDelivery.id}`
            );
            setSelectedDelivery(null);
          }}
          onSetPrice={() => {
            setPriceModalData({
              dailyMaterialId:
                selectedDelivery.dailyMaterialId || selectedDelivery.id,
              materialItems: selectedDelivery.materialItems || [],
            });
            setSelectedDelivery(null);
          }}
          onDelete={() => {
            setDeleteTarget(selectedDelivery);
            setSelectedDelivery(null);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          material={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {priceModalData && (
        <ItemsPriceModal
          dailyMaterialId={priceModalData.dailyMaterialId}
          materialItems={priceModalData.materialItems}
          onClose={() => setPriceModalData(null)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {fullImageUrl && (
        <FullImageModal url={fullImageUrl} onClose={() => setFullImageUrl(null)} />
      )}

      {/* ── Payment History Modal ── */}
      {showPaymentHistory && (
        <ModalShell
          title="Vendor payment history"
          size="lg"
          onClose={() => {
            setShowPaymentHistory(false);
            setSelectedPaymentSite("");
          }}
          footer={
            <button
              className="mat-btn mat-btn--ghost"
              onClick={() => setShowPaymentHistory(false)}
            >
              Close
            </button>
          }
        >
          <div className="mat-modal-toolbar">
            <button
              className="mat-btn mat-btn--primary"
              onClick={() => {
                setPaymentFormData({ amount: "", paymentMode: "", remark: "", siteName: "" });
                setIsEditingPayment(false);
                setCurrentPaymentId(null);
                setShowPaymentForm(true);
              }}
            >
              <Plus size={14} /> Add payment
            </button>
            <button
              className="mat-btn mat-btn--ghost"
              onClick={exportPaymentsToExcel}
              disabled={filteredPayments.length === 0}
              title="Export payment report to Excel"
            >
              <FileSpreadsheet size={15} /> Export Excel
            </button>

            <div className="mat-filter">
              <Filter size={14} />
              <select
                value={selectedPaymentSite}
                onChange={(e) => {
                  const site = e.target.value;
                  setSelectedPaymentSite(site);
                  if (site) {
                    setFilteredPayments(
                      vendorPayments.filter((p) => p.siteName === site)
                    );
                  } else {
                    setFilteredPayments(vendorPayments);
                  }
                }}
              >
                <option value="">All sites</option>
                {sites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <ChevronDown size={13} className="mat-filter__chevron" />
            </div>
          </div>

          {loadingPayments ? (
            <div className="mat-loading-inline">
              <div className="mat-spinner" />
              <span>Loading payments…</span>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="mat-empty-inline">
              No payments recorded yet
              {selectedPaymentSite && ` for site "${selectedPaymentSite}"`}.
            </div>
          ) : (
            <div className="mat-table-wrap">
              <table className="mat-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Vendor</th>
                    <th>Site</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Mode</th>
                    <th>Remark</th>
                    <th>Date</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((p) => (
                    <tr key={p.paymentId}>
                      <td>
                        <span className="mat-dm-no">{p.paymentId}</span>
                      </td>
                      <td>{p.vendorName || accountData?.vendorName || "—"}</td>
                      <td>{p.siteName || "—"}</td>
                      <td className="mat-amount mat-amount--success">
                        {fmtINR(p.amount)}
                      </td>
                      <td>
                        <Badge>{p.paymentMode || "—"}</Badge>
                      </td>
                      <td className="mat-td-remark">{p.remark || "—"}</td>
                      <td>
                        <span className="mat-time">
                          {p.paymentDate
                            ? new Date(p.paymentDate).toLocaleDateString("en-IN")
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <div className="mat-actions-cell">
                          <button
                            className="mat-icon-btn"
                            onClick={() => handleEditPayment(p)}
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="mat-icon-btn mat-icon-btn--danger"
                            onClick={() => handleDeletePayment(p.paymentId)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalShell>
      )}

      {/* ── Add / Edit Payment Form Modal ── */}
      {showPaymentForm && (
        <ModalShell
          title={isEditingPayment ? "Edit payment" : "Add new payment"}
          size="sm"
          onClose={() => {
            setShowPaymentForm(false);
            setIsEditingPayment(false);
            setCurrentPaymentId(null);
          }}
          footer={
            <>
              <button
                className="mat-btn mat-btn--ghost"
                onClick={() => {
                  setShowPaymentForm(false);
                  setIsEditingPayment(false);
                  setCurrentPaymentId(null);
                }}
              >
                Cancel
              </button>
              <button
                className="mat-btn mat-btn--primary"
                onClick={handleSavePayment}
                disabled={submittingPayment}
              >
                {submittingPayment
                  ? "Saving…"
                  : isEditingPayment
                  ? "Update payment"
                  : "Save payment"}
              </button>
            </>
          }
        >
          <div className="mat-form">
            <div className="mat-form__group">
              <label>
                Amount (₹) <span className="mat-required">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={paymentFormData.amount}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, amount: e.target.value })
                }
                placeholder="0.00"
              />
            </div>

            <div className="mat-form__group">
              <label>Site</label>
              <select
                value={paymentFormData.siteName}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, siteName: e.target.value })
                }
              >
                <option value="">Select site</option>
                {sites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="mat-form__group">
              <label>Payment mode</label>
              <select
                value={paymentFormData.paymentMode}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, paymentMode: e.target.value })
                }
              >
                <option value="">Select mode</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>

            <div className="mat-form__group">
              <label>Remark</label>
              <textarea
                rows={3}
                value={paymentFormData.remark}
                onChange={(e) =>
                  setPaymentFormData({ ...paymentFormData, remark: e.target.value })
                }
                placeholder="Optional note…"
              />
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────── */
/* Reusable Components                                  */
/* ─────────────────────────────────────────────────── */

const ModalShell = ({ title, children, footer, onClose, size = "md" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const sizeClass = { sm: "mat-modal--sm", md: "mat-modal--md", lg: "mat-modal--lg" }[size];

  return (
    <div className="mat-overlay">
      <div className={`mat-modal ${sizeClass}`} ref={ref}>
        <div className="mat-modal__head">
          <h2 className="mat-modal__title">{title}</h2>
          <button className="mat-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="mat-modal__body">{children}</div>
        {footer && <div className="mat-modal__foot">{footer}</div>}
      </div>
    </div>
  );
};

const DeliveryDetailModal = ({
  delivery,
  role,
  isEngineer,
  onClose,
  onEdit,
  onSetPrice,
  onDelete,
}) => {
  const total =
    delivery.materialItems?.reduce((s, i) => s + (Number(i.total) || 0), 0) || 0;

  const formatTime = (time) => {
    if (!time) return null;
    if (typeof time === "string") return time.substring(0, 5);
    if (typeof time === "object" && time.hour !== undefined) {
      return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
    }
    return null;
  };

  const timeStr = formatTime(delivery.time);

  return (
    <ModalShell
      title={`Delivery — ${
        delivery.dmNo || `DM-${delivery.dailyMaterialId || delivery.id}`
      }`}
      size="lg"
      onClose={onClose}
      footer={
        <div className="mat-modal-foot-group">
          <button className="mat-btn mat-btn--ghost" onClick={onEdit}>
            <Edit2 size={14} /> Edit entry
          </button>
          {!isEngineer && (
            <button className="mat-btn mat-btn--ghost" onClick={onSetPrice}>
              <IndianRupee size={14} /> Set prices
            </button>
          )}
          <button className="mat-btn mat-btn--danger" onClick={onDelete}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      }
    >
      <div className="mat-detail-grid">
        {[
          ["Vehicle", delivery.vehicleNo],
          ["Date / time", `${delivery.date || "—"} ${timeStr || ""}`],
          ["Site", delivery.siteName],
          ["Supplier", delivery.supplierName],
          ["Engineer", delivery.engineerName],
        ].map(([label, val]) => (
          <div className="mat-detail-item" key={label}>
            <span className="mat-detail-label">{label}</span>
            <span className="mat-detail-val">{val || "—"}</span>
          </div>
        ))}
      </div>

      {delivery.remark && (
        <div className="mat-remark-box">{delivery.remark}</div>
      )}

      <h3 className="mat-section-heading">Material items</h3>
      <div className="mat-table-wrap">
        <table className="mat-table mat-table--compact">
          <thead>
            <tr>
              <th>Material</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th>Unit</th>
              <th>Size / type</th>
              {!isEngineer && <th style={{ textAlign: "right" }}>Rate</th>}
              {!isEngineer && <th style={{ textAlign: "right" }}>Total</th>}
            </tr>
          </thead>
          <tbody>
            {(delivery.materialItems || []).map((it, i) => (
              <tr key={i}>
                <td>{it.dailyMaterialName || "—"}</td>
                <td style={{ textAlign: "right" }}>{it.materialQty || "—"}</td>
                <td>{it.unit || "—"}</td>
                <td>{it.size || "—"}</td>
                {!isEngineer && (
                  <td style={{ textAlign: "right" }} className="mat-amount">
                    {it.price ? (
                      `₹${it.price.toLocaleString("en-IN")}`
                    ) : (
                      <span className="mat-unset">Not set</span>
                    )}
                  </td>
                )}
                {!isEngineer && (
                  <td style={{ textAlign: "right" }} className="mat-amount">
                    {it.total ? `₹${it.total.toLocaleString("en-IN")}` : "—"}
                  </td>
                )}
              </tr>
            ))}
            {!isEngineer && (
              <tr className="mat-total-row">
                <td colSpan={4} style={{ textAlign: "right" }}>Grand total</td>
                <td />
                <td style={{ textAlign: "right" }} className="mat-amount mat-amount--success">
                  ₹{total.toLocaleString("en-IN")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
};

const ItemsPriceModal = ({ dailyMaterialId, materialItems, onClose, onSuccess }) => {
  const [prices, setPrices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const token = JSON.parse(localStorage.getItem("NagpurProperties"))?.token;

  useEffect(() => {
    const init = {};
    materialItems.forEach((it) => {
      if (it.itemId) init[it.itemId] = it.price ?? "";
    });
    setPrices(init);
  }, [materialItems]);

  const handleSubmit = async () => {
    const items = Object.entries(prices)
      .filter(([id, p]) => id && p !== "" && !isNaN(p) && Number(p) > 0)
      .map(([itemId, price]) => ({ itemId: Number(itemId), price: Number(price) }));

    if (!items.length) {
      alert("Please enter price for at least one item.");
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.put(
        "/DailyMaterial/items/prices",
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Prices updated successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update prices");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Set / update material prices"
      size="md"
      onClose={onClose}
      footer={
        <>
          <button className="mat-btn mat-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="mat-btn mat-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving…" : "Save prices"}
          </button>
        </>
      }
    >
      <div className="mat-price-list">
        {materialItems.map((it) => (
          <div className="mat-price-row" key={it.itemId}>
            <div>
              <p className="mat-price-name">{it.dailyMaterialName}</p>
              <p className="mat-price-meta">{it.materialQty} {it.unit}</p>
            </div>
            <div className="mat-price-input-wrap">
              <span className="mat-price-prefix">₹</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={prices[it.itemId] || ""}
                onChange={(e) =>
                  setPrices((prev) => ({ ...prev, [it.itemId]: e.target.value }))
                }
                className="mat-price-input"
              />
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
};

const DeleteConfirmModal = ({ material, onConfirm, onCancel }) => (
  <ModalShell
    title="Delete delivery?"
    size="sm"
    onClose={onCancel}
    footer={
      <>
        <button className="mat-btn mat-btn--ghost" onClick={onCancel}>Cancel</button>
        <button className="mat-btn mat-btn--danger" onClick={onConfirm}>
          <Trash2 size={14} /> Delete permanently
        </button>
      </>
    }
  >
    <div className="mat-delete-body">
      <div className="mat-delete-icon"><Trash2 size={32} /></div>
      <p>
        This will permanently remove the delivery record for{" "}
        <strong>{material.vehicleNo || "this entry"}</strong>. This action cannot be undone.
      </p>
    </div>
  </ModalShell>
);

const FullImageModal = ({ url, onClose }) => (
  <div className="mat-image-viewer" onClick={onClose}>
    <button className="mat-image-viewer__close" onClick={onClose}>
      <X size={24} />
    </button>
    <img src={url} alt="Full view" onClick={(e) => e.stopPropagation()} />
  </div>
);